import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FaPlayCircle, FaTruck, FaUsers, FaGlobe, FaAward, FaCheckCircle, FaShieldAlt } from 'react-icons/fa';
import Fancybox from './Fancybox';
import useScrollAnimation from '../hooks/useScrollAnimation';

const About = () => {
  const [showMore, setShowMore] = useState(false);
  const { t } = useTranslation();
  const aboutRef = useScrollAnimation(0.2);

  const readMore = useCallback(() => {
    setShowMore(true);
  }, []);

  // Debug logging
  console.log('About component rendering...', { t, showMore });

  return (
    <section className="about-modern" ref={aboutRef}>
      <div className="container">
        {/* Section Header */}
        <div className="section-header text-center mb-5 fade-in-up">
          <div className="section-badge">
            <FaAward className="me-2" />
            <span className="text-primary">{t('about.title')}</span>
          </div>
          <h2 className="section-title text-glow text-white">
            {t('about.title')}
          </h2>
          <div className="section-decoration"></div>
        </div>

        <div className="row align-items-center">
          {/* Video/Image Section */}
          <div className="col-lg-6 mb-5 mb-lg-0">
            <div className="media-container fade-in-left stagger-2">
              <div className="video-wrapper glass-container">
                <Fancybox
                  options={{
                    Carousel: {
                      infinite: false,
                    },
                  }}
                >
                  <img 
                    src="https://old.mileageriot.com/assets/front/img/uploads/16685453741803122197.webp" 
                    className="video-thumbnail" 
                    alt="Mileage Riot Video" 
                    onError={(e) => {
                      console.log('Image failed to load:', e.target.src);
                      e.target.src = 'https://hub.mileageriot.com/assets/images/banner/banner.jpg';
                    }}
                  />
                  <a 
                    data-fancybox="gallery" 
                    href="https://www.youtube.com/watch?v=F8sH7eC24PM&ab_channel=MileageRiot" 
                    className="play-overlay"
                  >
                    <div className="play-button-modern pulse-glow">
                      <FaPlayCircle />
                    </div>
                  </a>
                </Fancybox>
              </div>

              {/* Stats Cards */}
              <div className="stats-mini mt-4">
                <div className="stat-card glass-container stagger-child">
                  <FaTruck className="stat-icon text-primary" />
                  <div>
                    <div className="stat-number text-white">400+</div>
                    <div className="stat-label text-primary">{t('stats.users.title') || 'Members'}</div>
                  </div>
                </div>
                <div className="stat-card glass-container stagger-child">
                  <FaGlobe className="stat-icon text-primary" />
                  <div>
                    <div className="stat-number text-white">2021</div>
                    <div className="stat-label text-primary">{t('about.founded') || 'Founded'}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Content Section */}
          <div className="col-lg-6">
            <div className="content-wrapper fade-in-right stagger-3">
              {/* Premium highlights */}
              <div className="feature-highlights mb-4">
                <div className="feature-item stagger-child">
                      <FaCheckCircle className="stat-icon text-primary" />
                  <div className="feature-content">
                    <h4 className="text-white d-flex align-items-center">
                      {t('about.professionalCommunity') || 'Профессиональное сообщество'}
                    </h4>
                    <p className="text-white-50">{t('about.joinMembers') || 'Присоединяйтесь к 400+ активным участникам со всего мира'}</p>
                  </div>
                </div>
                <div className="feature-item stagger-child">
                    <FaShieldAlt className="stat-icon text-primary" />
                  <div className="feature-content">
                    <h4 className="text-white d-flex align-items-center">
                      {t('about.verifiedVTC') || 'Верифицированная VTC'}
                    </h4>
                    <p className="text-white-50">{t('about.officialTMP') || 'Официальная верифицированная транспортная компания TruckersMP'}</p>
                  </div>
                </div>
              </div>

              {/* Main content */}
              <div className="main-content">
                <p className="lead-text">
                  <span className="highlight-accent text-primary">
                    Mileage Riot - это верифицированная и полностью русскоязычная виртуальная транспортная компания, 
                    наполненная большими идеями и успешной реализацией. Мы работаем с 18 августа 2021 года, и за это время мы успешно 
                    провели множество конвоев с тщательной организацией.
                  </span>
                </p>
                <p className="secondary-text text-white-50">
                  Наша активная компания насчитывает более 400 сотрудников из разных стран.
                </p>

                {/* Read more section */}
                {!showMore && (
                  <div className="action-section mt-4">
                    <button 
                      className="btn read-more-btn text-white btn-hero-primary btn-sm" 
                      onClick={readMore}
                    >
                      <span>Подробнее</span>
                      <div className="btn-shine"></div>
                    </button>
                  </div>
                )}

                {showMore && (
                  <div className="extended-content fade-in-up">
                    <div className="content-divider"></div>
                    <p className="extended-text text-white-50">
                      В нашей компании вы найдете мощную технологическую систему, готовую взаимодействовать с вами и способствовать вашему развитию. 
                      Мы гордимся красивым дизайном нашего стиля. В нашу команду входят выдающиеся специалисты, администрация с богатым опытом 
                      и отзывчивым отношением к каждому водителю.
                    </p>
                    
                    {/* Call to action */}
                    <div className="cta-section mt-4">
                      <div className="cta-text">
                        <h5 className="text-white">Готовы присоединиться к путешествию?</h5>
                        <p className="text-white-50">Испытайте профессиональные грузоперевозки как никогда раньше</p>
                      </div>
                      <div className="cta-actions">
                        <a 
                          href="https://apply.mileageriot.com" 
                          className="btn btn-hero-primary btn-sm"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Присоединиться
                        </a>
                        <a 
                          href="https://discord.mileageriot.com" 
                          className="btn btn-hero-secondary btn-sm ms-3"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Discord
                        </a>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Background decoration */}
      <div className="section-bg-decoration"></div>
    </section>
  );
};

export default About;